"use client";

import { useMemo, useState } from "react";

import { BigAudioPlayer } from "@/components/audioPlayers/BigAudioPlayer";
import { ChevronButton } from "@/components/buttons/ChevronButton";
import { GradientSplash } from "@/components/GradientSplash";
import { SelectArtistModal } from "@/components/selectArtistModal/SelectArtistModal";
import { ShowImageArtistInstrumentModelMixedModel } from "@/components/showImage/ShowImageArtistInstrumentModelMixedModel";
import { Skeleton } from "@/components/skeletons/Skeleton";
import { Badge } from "@/components/text/Badge";
import { Heading2 } from "@/components/text/Heading2";
import { NormalText } from "@/components/text/NormalText";
import { SmallTitle } from "@/components/text/SmallTitle";
import { StartTutorialButton } from "@/components/tutorial/StartTutorialButton";
import { defaultArtist } from "@/constants/defaultValues";
import { conversionNamespace } from "@/constants/namespaces";
import { useNamedSelectedArtist } from "@/lib/zustand/useSelectedArtist";
import {
  ArtistInstumentSaved,
  MixedModelConstructedNoDates,
  ModelNoDates,
  UserNoDates,
} from "@/types/types";
import { getArtistInstrumentModelMixedModel } from "@/utils/artistInstrumentModelMixedModel/get/getArtistInstrumentModelMixedModel";
import { getModelPitchRange } from "@/utils/audio/quality/getModelPitchRange";
import { cacheBustUrl } from "@/utils/cacheBustUrl";
import { createMixedModelName } from "@/utils/createMixedModelName";
import { midiToNote } from "@/utils/midiToNote";

export function SelectedArtist({
  user,
  models,
  mixedModelsConstructed,
  savedArtistsInstrumentsIds,
  simplyUser,
  shouldShowLoadState,
  popularityByArtistId,
}: {
  user: UserNoDates | null;
  models: ModelNoDates[] | null;
  mixedModelsConstructed: MixedModelConstructedNoDates[] | null;
  savedArtistsInstrumentsIds: ArtistInstumentSaved["artistInstrumentId"][];
  simplyUser: boolean;
  shouldShowLoadState: boolean;
  popularityByArtistId: Record<string, number> | null;
}) {
  const [selectArtistModalOpen, setSelectArtistModalOpen] = useState(false);

  const useSelectedArtist = useNamedSelectedArtist((state) =>
    state.getNamedStore(conversionNamespace)
  );
  const selectedArtistId = useSelectedArtist((state) => state.selectedArtistId);
  const setSelectedArtistId = useSelectedArtist(
    (state) => state.setSelectedArtistId
  );

  const artistInstrumentModelMixedModel = getArtistInstrumentModelMixedModel(
    selectedArtistId,
    models,
    mixedModelsConstructed
  );

  if (artistInstrumentModelMixedModel === null) {
    // If we are in load state, we don't want to set the default artist
    if (!shouldShowLoadState) {
      setSelectedArtistId(defaultArtist.id);
    }
    return null;
  }

  const name =
    "name" in artistInstrumentModelMixedModel ?
      artistInstrumentModelMixedModel.name
    : createMixedModelName(artistInstrumentModelMixedModel);

  // Make sure the previewUrl cache busts only when the previewUrl changes (to avoid unnecessary fetches on every render)
  const previewUrl =
    "previewUrl" in artistInstrumentModelMixedModel ?
      artistInstrumentModelMixedModel.previewUrl
    : null;

  const cacheBustedPreviewUrl = useMemo(() => {
    return previewUrl === null ? null : cacheBustUrl(previewUrl);
  }, [previewUrl]);

  // Artists carry a curated `specific` range; custom models a trained one. Null (instruments, mixed models, models without a range) hides the label.
  const corePitchRange = getModelPitchRange({
    selectedModel: artistInstrumentModelMixedModel,
    shouldGetArtistsSweetSpot: false,
  });
  //////////////////////////////////////////////////////////////
  return (
    <>
      <SelectArtistModal
        namespace={conversionNamespace}
        open={selectArtistModalOpen}
        closeModal={() => setSelectArtistModalOpen(false)}
        user={user}
        models={models}
        mixedModelsConstructed={mixedModelsConstructed}
        handleClickArtist={(artistId) => {
          setSelectedArtistId(artistId);
        }}
        savedArtistsInstrumentsIds={savedArtistsInstrumentsIds}
        showMixedModelTab={true}
        popularityByArtistId={popularityByArtistId}
      />
      <div className="relative isolate">
        <GradientSplash />
        <div className="mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:px-8">
          <div className="flex w-full flex-col justify-between gap-12 md:flex-row md:items-center">
            <div data-tutorial="artist-info">
              <Heading2 className="mb-4">{name}</Heading2>
              {"description" in artistInstrumentModelMixedModel &&
                artistInstrumentModelMixedModel.description !== null && (
                  <NormalText elementType="p" className="mb-4 max-w-2xl">
                    {artistInstrumentModelMixedModel.description}
                  </NormalText>
                )}

              {"filters" in artistInstrumentModelMixedModel && (
                <div className="mb-4 flex flex-wrap gap-1">
                  {[
                    ...artistInstrumentModelMixedModel.filters.tags.primary,
                    ...artistInstrumentModelMixedModel.filters.tags.secondary,
                  ].map((tag) => (
                    <Badge key={tag} className="bg-gray-200">
                      {tag}
                    </Badge>
                  ))}
                </div>
              )}

              <div className="flex items-center space-x-2">
                <ChevronButton
                  onClick={() => setSelectArtistModalOpen(true)}
                  disabled={false}
                  className="max-w-[200px] font-medium"
                  dataTutorial="artist-selection-button"
                >
                  Switch voice
                </ChevronButton>
                {shouldShowLoadState ?
                  <Skeleton className="h-9 w-28 rounded-lg" />
                : <StartTutorialButton user={user} simplyUser={simplyUser} />}
              </div>
            </div>

            <div className="flex w-fit flex-col items-start md:items-end">
              <div className="mb-5 w-60 sm:w-72">
                <ShowImageArtistInstrumentModelMixedModel
                  artistInstrumentModelMixedModel={
                    artistInstrumentModelMixedModel
                  }
                />
              </div>
              {cacheBustedPreviewUrl !== null && (
                <BigAudioPlayer
                  // Cloudflare sometimes serves a cached 200 OK for Range requests on preview files (I think), which breaks (ERR_HTTP2_PROTOCOL_ERROR). We append a cache-busting param to force bypassing the broken Cloudflare cache. Kind of a hack.
                  source={cacheBustedPreviewUrl}
                  includePreviewText={false}
                  bigWaves={false}
                  filename={null}
                  className="w-full rounded-3xl bg-white px-4 py-2 shadow-sm ring-1 ring-gray-900/5"
                />
              )}
              {corePitchRange !== null && (
                <div className="mt-4 flex flex-col items-start md:items-end">
                  <NormalText elementType="p" className="text-gray-400">
                    CORE PITCH RANGE
                  </NormalText>
                  <SmallTitle elementType="span" className="text-base">
                    {midiToNote(corePitchRange.low)} -{" "}
                    {midiToNote(corePitchRange.high)}
                  </SmallTitle>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
